import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { api, apiError } from "@/lib/api";
import SponsorCard from "@/components/sponsors/SponsorCard";
import EmptyState from "@/components/common/EmptyState";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Favorites() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/favorites");
      setItems(data.favorites);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (sponsor) => {
    try {
      await api.delete(`/favorites/${sponsor.user_id}`);
      setItems((l) => l.filter((s) => s.user_id !== sponsor.user_id));
      toast.success("Removed from favorites.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const message = async (sponsor) => {
    try {
      const { data } = await api.post("/conversations", { other_user_id: sponsor.user_id });
      navigate(`/messages?c=${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Favorites</h1>
      <p className="mt-1.5 text-[#4B5A60]">
        Sponsors you saved. We will notify you when a favorited sponsor starts accepting sponsees.
      </p>
      <div className="mt-6">
        {loading ? (
          <Spinner label="Loading favorites" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Heart}
            testId="favorites-empty-state"
            title="No saved sponsors yet"
            description="Tap the heart on any sponsor profile to keep them here while you decide who feels like the right fit."
            action={
              <Button
                onClick={() => navigate("/sponsors")}
                className="bg-[#1F6F78] hover:bg-[#185B62] text-white"
              >
                Browse sponsors
              </Button>
            }
          />
        ) : (
          <div data-testid="favorites-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <SponsorCard key={s.user_id} sponsor={s} onFavorite={remove} onMessage={message} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
